import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY as STRIPE_PK } from '../config/env';
import { calculateDeliveryFeeCents } from '../utils/delivery';
import { submitPayment } from '../utils/checkoutSubmit';
import { addonsPayload } from '../utils/addons';
import './ExpressCheckout.css';

// Load Stripe.js once at module load (early) so wallet detection can start
// as soon as possible.
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function variantLabelFor(width, projection) {
  const w = Number(width), p = Number(projection);
  return Number.isFinite(w) && Number.isFinite(p) ? `${w} × ${p} m` : '';
}

// Inner — needs to live inside <Elements> to use the Stripe hooks. Renders the
// real Apple Pay / Google Pay buttons and drives the same server-side payment
// path as the full checkout (via submitPayment). The wallet sheet collects the
// buyer's contact + shipping details itself.
function ExpressInner({ product, width, projection, colourName, colourCode, addons, addonQty, amountCents }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [error, setError] = useState('');

  // Keep the wallet's displayed amount in sync as the selected variant changes
  // (the <Elements> amount is only read on mount).
  useEffect(() => {
    if (elements && amountCents > 0) elements.update({ amount: amountCents });
  }, [elements, amountCents]);

  // Fallback: on a browser with no wallet, Stripe may never fire onReady, which
  // would leave the skeleton up forever. Collapse after a grace period. When a
  // wallet IS available, onReady fires well before this (a few seconds).
  useEffect(() => {
    const t = setTimeout(() => setStatus((s) => (s === 'loading' ? 'unavailable' : s)), 25000);
    return () => clearTimeout(t);
  }, []);

  const variantLabel = variantLabelFor(width, projection);
  // amountCents is the whole order before delivery: the awning plus any paid
  // add-ons. The wallet sheet lists them separately, so the awning's own line
  // is what is left once the add-ons are taken off.
  const paidAddons = (addons || []).filter((l) => !l.included && l.lineTotal > 0);
  const addonCents = paidAddons.reduce((sum, l) => sum + Math.round(l.lineTotal * 100), 0);

  const handleClick = (event) => {
    event.resolve({
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: true,
      allowedShippingCountries: ['AU'],
      // Delivery isn't known until the wallet gives us a postcode; start at the
      // flat rate and correct it in onShippingAddressChange.
      shippingRates: [{ id: 'standard', amount: 15000, displayName: 'Delivery' }],
      lineItems: [
        { name: `${product.name}${variantLabel ? ' - ' + variantLabel : ''}`, amount: amountCents - addonCents },
        ...paidAddons.map((l) => ({
          name: `${l.quantity > 1 ? `${l.quantity} x ` : ''}${l.name}`,
          amount: Math.round(l.lineTotal * 100),
        })),
      ],
    });
  };

  const handleShippingAddressChange = (event) => {
    const pc = event.address && event.address.postal_code;
    if (!pc || !/^\d{4}$/.test(pc)) { event.reject(); return; }
    const fee = calculateDeliveryFeeCents(pc);
    // Zone 3: freight quoted separately - cannot be charged via express checkout.
    if (fee == null) { event.reject(); return; }
    event.resolve({
      shippingRates: [{
        id: fee === 0 ? 'free' : 'standard',
        amount: fee,
        displayName: fee === 0 ? 'Free delivery' : 'Delivery',
      }],
    });
  };

  const handleConfirm = async (event) => {
    setError('');
    const sa = (event.shippingAddress && event.shippingAddress.address) || {};
    const name = (event.shippingAddress && event.shippingAddress.name)
      || (event.billingDetails && event.billingDetails.name) || '';
    const customer = {
      full_name: name,
      email: event.email || (event.billingDetails && event.billingDetails.email) || '',
      phone: event.phone || (event.billingDetails && event.billingDetails.phone) || '',
      address_line1: sa.line1 || '',
      address_line2: sa.line2 || '',
      suburb: sa.city || '',
      state: sa.state || 'VIC',
      postcode: sa.postal_code || '',
    };
    try {
      await submitPayment({
        stripe,
        elements,
        payload: {
          product_slug: product.slug || '',
          width: width || null,
          projection: projection || null,
          colour: colourName ? { name: colourName, code: colourCode } : null,
          addons: addonsPayload(addonQty),
          customer,
          notes: '',
          referrer: typeof document !== 'undefined' ? document.referrer : '',
        },
        returnUrl: `${window.location.origin}/checkout/success`,
        receiptEmail: customer.email,
      });
      navigate('/checkout/success');
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    }
  };

  return (
    <div className={`xco xco--${status}`}>
      {status === 'loading' && (
        <div className="xco-skeleton" aria-hidden="true">
          <span className="xco-skeleton-bar" />
          <span className="xco-skeleton-bar" />
        </div>
      )}
      <ExpressCheckoutElement
        options={{
          paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', amazonPay: 'never', paypal: 'never' },
          buttonHeight: 46,
          layout: { maxColumns: 1, maxRows: 2, overflow: 'never' },
        }}
        onReady={({ availablePaymentMethods }) => {
          const a = availablePaymentMethods || {};
          setStatus(a.applePay || a.googlePay ? 'ready' : 'unavailable');
        }}
        onLoadError={() => setStatus('unavailable')}
        onClick={handleClick}
        onShippingAddressChange={handleShippingAddressChange}
        onConfirm={handleConfirm}
      />
      {error && <p className="xco-error">{error}</p>}
    </div>
  );
}

// ExpressCheckout — drop-in Apple Pay / Google Pay block for a single
// configured product variant and its add-ons (`addons` are the display lines
// from utils/addons, `addonQty` the { slug: qty } the server prices). Renders nothing when Stripe isn't configured or
// there's no price. Reserves its own height so it can't shift the layout while
// the wallets load.
export default function ExpressCheckout({ product, width, projection, colourName, colourCode, addons, addonQty, amountCents }) {
  // Options are read once on mount; the amount is kept live via elements.update
  // in the inner component, so we freeze the initial amount here to avoid
  // re-initialising Stripe every time the variant price changes.
  const initialAmount = useRef(amountCents);
  const options = useMemo(() => ({
    mode: 'payment',
    amount: initialAmount.current > 0 ? initialAmount.current : 100,
    currency: 'aud',
    paymentMethodTypes: ['card'],
    appearance: {
      theme: 'stripe',
      variables: { colorPrimary: 'var(--asg-cta)', fontFamily: 'Outfit, sans-serif', borderRadius: '8px' },
    },
  }), []);

  if (!stripePromise || !amountCents || amountCents <= 0) return null;

  return (
    <Elements stripe={stripePromise} options={options}>
      <ExpressInner
        product={product}
        width={width}
        projection={projection}
        colourName={colourName}
        colourCode={colourCode}
        addons={addons}
        addonQty={addonQty}
        amountCents={amountCents}
      />
    </Elements>
  );
}
